import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { MicButton } from "@/components/MicButton";
import { useAuth } from "@/context/AuthContext";
import { Container, PageHeader, Spinner, GenerateButton } from "@/components/shared";
import { toast } from "sonner";
import { Plus, ChevronUp, MessageSquare, Send, X, Flag, ShieldCheck } from "lucide-react";

const REPORT_REASONS = [
  ["insulte", "Insulte"], ["harcelement", "Harcèlement"], ["racisme", "Racisme"],
  ["menace", "Menace"], ["spam", "Spam"], ["desinformation", "Désinformation"],
  ["publicite", "Publicité"], ["autre", "Autre"],
];

export default function Community() {
  const { user, refresh } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [content, setContent] = useState("");
  const [answerFor, setAnswerFor] = useState(null);
  const [answerText, setAnswerText] = useState("");
  const [reportFor, setReportFor] = useState(null); // {type, id}

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/community"); setPosts(data.posts || []); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const acceptRules = async () => {
    try { await api.put("/me/accept-rules"); await refresh?.(); toast.success("Merci ! Bienvenue dans la communauté 💛"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const notify = (data) => {
    if (data?.status && data.status !== "published") toast.success(data.pending_notice || "Votre message sera visible après vérification par un modérateur.");
    else toast.success("Publié.");
  };

  const createPost = async () => {
    if (!content.trim()) return;
    try {
      const { data } = await api.post("/community/post", { content });
      setContent(""); setShowNew(false); notify(data); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const submitAnswer = async (postId) => {
    if (!answerText.trim()) return;
    try {
      const { data } = await api.post(`/community/${postId}/answer`, { content: answerText });
      setAnswerText(""); setAnswerFor(null); notify(data); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const vote = async (answerId) => {
    try {
      await api.post("/community/answer/vote", { target_id: answerId });
      setPosts((ps) => ps.map((p) => ({ ...p, answers: p.answers.map((a) => a.answer_id === answerId ? { ...a, votes: a.votes + 1 } : a).sort((x, y) => y.votes - x.votes) })));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const report = async (reason) => {
    if (!reportFor) return;
    try {
      await api.post("/community/report", { target_type: reportFor.type, target_id: reportFor.id, reason });
      toast.success("Merci, votre signalement a été transmis aux modérateurs.");
    } catch (e) { toast.error(apiErr(e)); } finally { setReportFor(null); }
  };

  return (
    <Container>
      <div className="flex items-center justify-between mb-2">
        <PageHeader eyebrow="Communauté" title="Comment auriez-vous répondu ?"
          subtitle="Publiez anonymement une situation, la communauté propose et vote les meilleures réponses." />
      </div>

      {user && user.rules_accepted === false && (
        <div className="card-surface p-4 mb-5 flex flex-wrap items-center gap-3 border-gold/25" data-testid="rules-gate">
          <ShieldCheck size={18} className="text-gold shrink-0" />
          <p className="text-sm text-white/70 flex-1 min-w-[200px]">
            Le désaccord est bienvenu, les insultes et le harcèlement ne le sont pas.{" "}
            <Link to="/regles" className="text-gold hover:underline">Lire les règles de la communauté</Link>.
          </p>
          <button data-testid="accept-rules-btn" onClick={acceptRules}
            className="gold-gradient text-black rounded-full px-4 py-2 text-sm font-medium">J'accepte les règles</button>
        </div>
      )}

      <div className="mb-6 flex items-center gap-3">
        <GenerateButton data-testid="new-post-button" onClick={() => setShowNew((s) => !s)}>
          {showNew ? <X size={16} /> : <Plus size={16} />} {showNew ? "Annuler" : "Nouvelle publication"}
        </GenerateButton>
        <Link to="/regles" className="text-white/40 hover:text-gold text-sm inline-flex items-center gap-1.5"><ShieldCheck size={14} /> Règles</Link>
      </div>

      {showNew && (
        <div className="card-surface p-5 mb-6 fade-up">
          <textarea data-testid="post-content-input" rows={3} value={content} onChange={(e) => setContent(e.target.value)}
            placeholder="On m'a dit… Comment auriez-vous répondu ?"
            className="w-full bg-transparent outline-none resize-none text-sm placeholder:text-white/30" />
          <div className="flex items-center justify-between gap-3 mt-3">
            <MicButton testid="post-content-mic" value={content} onChange={setContent} />
            <GenerateButton data-testid="submit-post-button" onClick={createPost} disabled={!content.trim()}>Publier</GenerateButton>
          </div>
        </div>
      )}

      {loading && <Spinner label="Chargement..." />}

      <div className="space-y-4">
        {posts.map((p, i) => (
          <div key={p.post_id} data-testid={`community-post-${i}`} className="card-surface p-5 fade-up">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-white/40 text-xs">
                <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[10px]">?</span>
                Anonyme
              </div>
              <button data-testid={`report-post-${i}`} onClick={() => setReportFor({ type: "post", id: p.post_id })}
                className="text-white/25 hover:text-red-400 transition-colors" title="Signaler"><Flag size={14} /></button>
            </div>
            <p className="text-white/90 mb-4">{p.content}</p>

            <div className="space-y-2 mb-3">
              {p.answers?.map((a, j) => (
                <div key={a.answer_id} data-testid={`answer-${i}-${j}`}
                  className={`flex items-start gap-3 rounded-xl p-3 ${j === 0 && a.votes > 0 ? "border border-gold/30 bg-gold/[0.06]" : "bg-white/[0.03]"}`}>
                  <button data-testid={`vote-${a.answer_id}`} onClick={() => vote(a.answer_id)}
                    className="flex flex-col items-center text-white/50 hover:text-gold shrink-0">
                    <ChevronUp size={16} /><span className="text-xs">{a.votes}</span>
                  </button>
                  <p className="text-white/80 text-sm flex-1">{a.content}</p>
                  <button data-testid={`report-answer-${a.answer_id}`} onClick={() => setReportFor({ type: "answer", id: a.answer_id })}
                    className="text-white/20 hover:text-red-400 transition-colors shrink-0" title="Signaler"><Flag size={12} /></button>
                </div>
              ))}
            </div>

            {answerFor === p.post_id ? (
              <div className="flex items-center gap-2">
                <input data-testid={`answer-input-${i}`} value={answerText} onChange={(e) => setAnswerText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitAnswer(p.post_id)}
                  placeholder="Votre réponse..." autoFocus
                  className="flex-1 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2 outline-none text-sm" />
                <MicButton testid={`answer-mic-${i}`} value={answerText} onChange={setAnswerText} />
                <button data-testid={`submit-answer-${i}`} onClick={() => submitAnswer(p.post_id)}
                  className="w-9 h-9 rounded-full gold-gradient flex items-center justify-center text-black"><Send size={15} /></button>
              </div>
            ) : (
              <button data-testid={`reply-toggle-${i}`} onClick={() => { setAnswerFor(p.post_id); setAnswerText(""); }}
                className="flex items-center gap-2 text-white/50 hover:text-white text-sm">
                <MessageSquare size={15} /> Répondre ({p.answer_count})
              </button>
            )}
          </div>
        ))}
        {!loading && posts.length === 0 && <p className="text-white/30 text-sm">Aucune publication pour l'instant. Soyez le premier !</p>}
      </div>

      {reportFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
          onClick={() => setReportFor(null)}>
          <div className="w-full max-w-sm card-surface p-6" onClick={(e) => e.stopPropagation()} data-testid="report-modal">
            <div className="flex items-center gap-2 mb-1"><Flag size={18} className="text-red-400" /><h3 className="font-display text-lg">Signaler ce contenu</h3></div>
            <p className="text-white/45 text-sm mb-4">Pourquoi signalez-vous ce contenu ?</p>
            <div className="grid grid-cols-2 gap-2">
              {REPORT_REASONS.map(([val, label]) => (
                <button key={val} data-testid={`report-reason-${val}`} onClick={() => report(val)}
                  className="rounded-xl border border-white/10 px-3 py-2.5 text-sm hover:border-red-400/40 hover:text-red-300 transition-colors">{label}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}
